/**
 * @file: useOrderVoiceUrls.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { OrderVoiceUrls } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';

/**
 * Loads voice-asset URLs for an order. No-ops (returns `null`) when the
 * store has voice synthesis disabled or `orderNo` is not yet known.
 * Refetches when `orderNo` changes.
 */
export const useOrderVoiceUrls = (
  orderNo: string | undefined,
): OrderVoiceUrls | null => {
  const { isVoiceSynthesisEnabled } = useVoiceFeatures();
  const { orderService } = useServiceFactory();
  const [urls, setUrls] = useState<OrderVoiceUrls | null>(null);

  useEffect(() => {
    setUrls(null);
    if (!orderNo || !isVoiceSynthesisEnabled) return;
    let canceled = false;
    void (async () => {
      try {
        const result = await orderService.getOrderVoiceUrls(orderNo);
        if (!canceled) setUrls(result ?? null);
      } catch (e) {
        if (!canceled) {
          console.warn('failed to load order voice urls:', e);
        }
      }
    })();
    return () => {
      canceled = true;
    };
  }, [orderNo, isVoiceSynthesisEnabled, orderService]);

  return urls;
};
